/**
 * Admin-scoped field-definition API client.
 *
 * Mirrors `src/api/fieldDefinitionApi.ts` but hits `/api/admin/field-
 * definitions` instead of `/api/field-definition`. The underlying handler
 * is the same Express Router (re-mounted under the admin parent in R7), so
 * the request/response shapes are identical.
 *
 * We keep a separate thin client here rather than parameterizing the
 * tenant-side one because the admin surface always uses the admin mount
 * (which applies super-admin role gating + the ADMIN_VIEW_ENABLED kill
 * switch), and mixing the two URLs in one module would be easy to mis-use.
 */
import axiosClient from '../../../api/axiosClient';

export interface FieldDefinition {
  id: number;
  json_type: string;
  group_name: string;
  group_order: number;
  field_key: string;
  field_label: string;
  field_description: string | null;
  field_type: string;
  field_order: number;
  field_options: { label: string; value: string | number | boolean }[] | null;
  field_default: unknown;
  visible_when_field: string | null;
  visible_when_op: string | null;
  visible_when_value: unknown;
  created_at: string;
}

const BASE = '/api/admin/field-definitions';

export const fieldDefinitionAdminApi = {
  getAll: async (jsonType?: string): Promise<FieldDefinition[]> => {
    const url = jsonType ? `${BASE}?type=${jsonType}` : BASE;
    const { data } = await axiosClient.get(url);
    return data;
  },

  create: async (payload: Record<string, unknown>): Promise<FieldDefinition> => {
    const { data } = await axiosClient.post(BASE, payload);
    return data;
  },

  update: async (id: number, payload: Record<string, unknown>): Promise<FieldDefinition> => {
    const { data } = await axiosClient.put(`${BASE}/${id}`, payload);
    return data;
  },

  remove: async (id: number): Promise<void> => {
    await axiosClient.delete(`${BASE}/${id}`);
  },
};
